import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import {
  CLAIM_EMAIL_IN_USE_MESSAGE,
  getSupplierMatchStatusDisplay,
  isSupplierMatchActionable
} from "@/lib/supplier-match-status";
import type { SalonStockerHttpTypes } from "@repo/api-sdk";
import { TruncateTooltip } from "@repo/design-system";
import { cn } from "@repo/design-system/utils";
import { memo } from "react";

interface SupplierMatchCardProps {
  view: SalonStockerHttpTypes.StoreSupplierMatchStatusView;
  /** Whether the grid's auto-check pass currently has this card in flight. */
  checking?: boolean;
  onSelect: (sellerId: string) => void;
}

/**
 * One seller's Supplier Match card - an icon-chip tile mirroring
 * `ShopByCategory`'s grid pattern. A seller with nothing to do - no lookup
 * capability, and no trade-account claim to make or remove - still shows its
 * last-known status (ADR-0005 "Supplier scope"), just muted - the card stays
 * clickable so the detail dialog can still explain why.
 */
export const SupplierMatchCard = memo(function SupplierMatchCard({
  view,
  checking,
  onSelect
}: SupplierMatchCardProps) {
  const initial = view.seller_name.trim().charAt(0).toUpperCase() || "?";
  const { variant, label } = getSupplierMatchStatusDisplay(view, {
    checking
  });
  const muted = !isSupplierMatchActionable(view);

  return (
    <button
      type="button"
      onClick={() => onSelect(view.seller_id)}
      aria-label={`${view.seller_name} - ${label}${
        view.claim_blocked ? `. ${CLAIM_EMAIL_IN_USE_MESSAGE}` : ""
      }`}
      // `h-full` on the button and the card: grid items stretch to the row's
      // height, so a card with the extra blocked-claim message no longer leaves
      // its neighbours' borders short.
      className="block h-full w-full text-left"
    >
      <Card
        className={cn(
          "h-full transition-shadow",
          muted ? "opacity-60" : "cursor-pointer hover:shadow-md"
        )}
      >
        <CardContent className="flex flex-col items-center gap-2 p-4 text-center">
          <div
            className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-purple-100 text-lg font-bold text-purple-700 dark:bg-purple-900/40 dark:text-purple-300"
            aria-hidden="true"
          >
            {initial}
          </div>
          <TruncateTooltip
            as="p"
            className="w-full text-sm font-semibold text-gray-900 dark:text-white"
          >
            {view.seller_name}
          </TruncateTooltip>
          <Badge variant={variant}>{label}</Badge>
          {view.claim_blocked ? (
            <p className="text-xs text-red-600 dark:text-red-400">
              {CLAIM_EMAIL_IN_USE_MESSAGE}
            </p>
          ) : null}
        </CardContent>
      </Card>
    </button>
  );
});
